import type { RouteLocationNormalized } from 'vue-router'

import ability from '@/plugins/casl/ability'
import { useUserStore } from '@/stores/user'
import { useInsuranceVerificationEntitlementStore } from '@/stores/vector/insuranceVerificationEntitlement'
import { BehaviorScreen } from '@/utils/instrumentation'

function redirectIfNotAllowed(): void {
  // Use CASL as the source of truth — if user can manage or read VerificationSession,
  // they're allowed into provider routes. Data scoping (which provider) is enforced server-side.
  // AbilityService grants copay_assistance as `manage Opportunity` (not `CopayTask` —
  // that subject name is never emitted by the backend).
  const canAccess
    = ability.can('manage', 'VerificationSession')
    || ability.can('read', 'VerificationSession')
    || ability.can('manage', 'Opportunity')
    || ability.can('read', 'Opportunity')

  if (!canAccess)
    window.location.href = import.meta.env.VITE_APP_URL
}

/**
 * Ops-only gate for a route that lives on a provider path (VH-2340).
 *
 * The investigation request detail page is reached from a Notion card by the
 * ops team, not by the hospital staff who raised the request — they hear the
 * outcome by email. Uses the Vector-access check rather than
 * redirectIfNotAllowed()'s CASL abilities, which any entitled provider user
 * passes.
 */
function redirectIfNotVectorAdmin(): void {
  const user = useUserStore()

  if (user.data?.access?.vector !== true)
    window.location.href = import.meta.env.VITE_APP_URL
}

async function guardInsuranceVerificationEntry(to: RouteLocationNormalized) {
  redirectIfNotAllowed()

  const user = useUserStore()

  // Provider-side users are already gated server-side by EnsureProviderEntitlement
  // on every insurance.* API call — this guard only closes the Vector Admin bypass gap.
  if (user.data?.access?.vector !== true)
    return true

  const providerUuid = Array.isArray(to.params.provider_uuid) ? to.params.provider_uuid[0] : to.params.provider_uuid
  const entitlementStore = useInsuranceVerificationEntitlementStore()
  const enabled = await entitlementStore.check(providerUuid)

  if (!enabled) {
    return {
      name: 'iv-not-entitled',
      params: { provider_uuid: providerUuid },
    }
  }

  return true
}

const provider = [
  {
    path: '/provider/:provider_uuid/patients-to-contact',
    name: 'provider-patients-to-contact',
    component: () => import('@/pages/provider/PatientToContactList.vue'),
    meta: {
      layout: 'provider',
      title: 'Patient Outreach',
      resource: 'Provider',
      action: 'read',
      subject: 'Opportunity',
    },
    beforeEnter() {
      redirectIfNotAllowed()
    },
  },

  // ── Insurance Verification ────────────────────────────────────────────────

  {
    // VH-2583: the path is load-bearing — the nightly discovery digest emails this exact URL
    // (AutoDiscoverSelfPayInsuranceCommand builds it from config('app.client_url')), so links in
    // already-delivered mail break if it moves.
    path: '/provider/:provider_uuid/insurance/inbox',
    name: 'iv-inbox',
    component: () => import('@/pages/provider/insurance/InboxPage.vue'),

    // Deliberately no `layoutWrapperClasses` — like iv-bulk-detail, and unlike the workspace
    // routes. `layout-content-height-fixed` absolutely-positions the page root at inset:0 with
    // overflow:hidden, which only works for pages that supply their own bounded internal scroll
    // container (see the note in layouts/provider.vue and VH-1817). This is an ordinary
    // top-to-bottom list page, so it wants the default padding-and-scroll behaviour instead.
    meta: { screen: BehaviorScreen.IvInbox, layout: 'provider', title: 'Insurance Inbox', resource: 'Provider', action: 'manage', subject: 'InsuranceInbox' },
    beforeEnter: to => guardInsuranceVerificationEntry(to),
  },
  {
    // VH-2263/VH-2278: single unified "look up a patient" coverage-check flow —
    // replaces the old separate verify/discover mode-select UX.
    //
    // Entry only. This used to also carry an optional :uuid and render the result view
    // from the same component, but VH-2357 made plan_uuid the identity for every plan
    // (verified or not), so there is no session-uuid-keyed view left to render: once a
    // lookup resolves, LookupFlow pushes iv-plan-workspace with patient_uuid + plan_uuid.
    path: '/provider/:provider_uuid/insurance',
    name: 'iv-lookup',
    component: () => import('@/pages/provider/insurance/LookupFlow.vue'),
    meta: { screen: BehaviorScreen.IvVerify, layout: 'provider', title: 'Insurance Verification', resource: 'Provider', action: 'read', subject: 'VerificationSession', layoutWrapperClasses: ['layout-content-height-fixed', 'layout-footer-hidden'] },
    beforeEnter: to => guardInsuranceVerificationEntry(to),
  },
  {
    // Bulk Discover lands on batch run history first (matching the Figma flow) —
    // starting a new upload is an explicit action from there, not the default landing.
    path: '/provider/:provider_uuid/insurance/bulk',
    name: 'iv-bulk',
    component: () => import('@/pages/provider/insurance/BulkDiscoverLandingPage.vue'),
    meta: { screen: BehaviorScreen.IvBulk, layout: 'provider', title: 'Bulk Discover', resource: 'Provider', action: 'read', subject: 'VerificationSession', layoutWrapperClasses: ['layout-content-height-fixed', 'layout-footer-hidden'] },
    beforeEnter() { redirectIfNotAllowed() },
  },
  {
    path: '/provider/:provider_uuid/insurance/bulk/new',
    name: 'iv-bulk-new',
    component: () => import('@/pages/provider/insurance/BulkPage.vue'),
    meta: { screen: BehaviorScreen.IvBulkNew, layout: 'provider', title: 'Bulk Insurance Discovery', resource: 'Provider', action: 'read', subject: 'VerificationSession', layoutWrapperClasses: ['layout-content-height-fixed', 'layout-footer-hidden'] },
    beforeEnter: to => guardInsuranceVerificationEntry(to),
  },
  {
    // Batch upload history now lives at the Bulk Discover landing page itself.
    path: '/provider/:provider_uuid/insurance/bulk/runs',
    redirect: (to: RouteLocationNormalized) => ({ name: 'iv-bulk', params: to.params }),
  },
  {
    path: '/provider/:provider_uuid/insurance/bulk/:batchId',
    name: 'iv-bulk-detail',
    component: () => import('@/pages/provider/insurance/BulkBatchDetailPage.vue'),
    meta: { screen: BehaviorScreen.IvBulkDetail, layout: 'provider', title: 'Discovery Report', resource: 'Provider', action: 'read', subject: 'VerificationSession' },
    beforeEnter: to => guardInsuranceVerificationEntry(to),
  },
  {
    path: '/provider/:provider_uuid/insurance/price-list',
    name: 'iv-price-list',
    component: () => import('@/pages/provider/insurance/PriceListPage.vue'),
    meta: { screen: BehaviorScreen.IvPriceList, layout: 'provider', title: 'Self-Pay Price List', resource: 'Provider', action: 'read', subject: 'VerificationSession', layoutWrapperClasses: ['layout-content-height-fixed', 'layout-footer-hidden'] },
    beforeEnter: to => guardInsuranceVerificationEntry(to),
  },
  {
    path: '/provider/:provider_uuid/insurance-not-entitled',
    name: 'iv-not-entitled',
    component: () => import('@/pages/provider/insurance/NotEntitledPage.vue'),
    meta: { layout: 'provider', title: 'Insurance Verification' },
    beforeEnter() { redirectIfNotAllowed() },
  },
  {
    path: '/provider/:provider_uuid/insurance/history',
    name: 'iv-history',
    component: () => import('@/pages/provider/insurance/PatientHistoryPage.vue'),
    meta: { screen: BehaviorScreen.IvHistory, layout: 'provider', title: 'Patient History', resource: 'Provider', action: 'read', subject: 'VerificationSession', layoutWrapperClasses: ['layout-content-height-fixed', 'layout-footer-hidden'] },
    beforeEnter() { redirectIfNotAllowed() },
  },
  {
    path: '/provider/:provider_uuid/insurance/history/:uuid',
    name: 'iv-history-detail',
    component: () => import('@/pages/provider/insurance/PatientPlanDetailPage.vue'),
    meta: { screen: BehaviorScreen.IvHistoryDetail, layout: 'provider', title: 'Patient Plan Detail', resource: 'Provider', action: 'read', subject: 'VerificationSession', layoutWrapperClasses: ['layout-content-height-fixed', 'layout-footer-hidden'] },
    beforeEnter() { redirectIfNotAllowed() },
  },
  {
    path: '/provider/:provider_uuid/insurance/patient/:patient_uuid',
    name: 'iv-patient-detail',
    component: () => import('@/pages/provider/insurance/PatientPlanDetailPage.vue'),
    meta: { screen: BehaviorScreen.IvPatientDetail, layout: 'provider', title: 'Patient Insurance Detail', resource: 'Provider', action: 'read', subject: 'VerificationSession', layoutWrapperClasses: ['layout-content-height-fixed', 'layout-footer-hidden'] },
    beforeEnter() { redirectIfNotAllowed() },
  },
  {
    // VH-2340: the ops surface a Notion card links to. Ops-gated rather than
    // entitlement-gated — see redirectIfNotVectorAdmin. No list route exists by
    // design; the Notion board is the queue.
    path: '/provider/:provider_uuid/insurance/investigation/:investigation_uuid',
    name: 'iv-investigation-detail',
    component: () => import('@/pages/provider/insurance/InvestigationRequestPage.vue'),
    meta: { screen: BehaviorScreen.IvInvestigationDetail, layout: 'provider', title: 'Insurance Investigation Request', resource: 'Provider', action: 'read', subject: 'VerificationSession' },
    beforeEnter() { redirectIfNotVectorAdmin() },
  },
  {
    path: '/provider/:provider_uuid/reports',
    name: 'provider-reports',
    component: () => import('@/pages/provider/reports/ReportsPage.vue'),
    meta: { screen: BehaviorScreen.ProviderReports, layout: 'provider', title: 'Reports & Invoices', resource: 'Provider', action: 'read', subject: 'ProviderReport' },

    // VH-3551: released to hospital admins now that VH-3450's production QA has passed. The
    // ability mirrors the endpoint's `role:provider_admin`; Vector admins hold it too.
    beforeEnter() {
      if (!ability.can('read', 'ProviderReport'))
        window.location.href = import.meta.env.VITE_APP_URL
    },
  },
  {
    path: '/provider/:provider_uuid/settings',
    name: 'provider-settings',
    component: () => import('@/pages/provider/insurance/SettingsPage.vue'),
    meta: { screen: BehaviorScreen.IvSettings, layout: 'provider', title: 'Provider Settings', resource: 'Provider', action: 'manage', subject: 'User', layoutWrapperClasses: ['layout-content-height-fixed', 'layout-footer-hidden'] },
    beforeEnter() {
      if (!ability.can('manage', 'User'))
        window.location.href = import.meta.env.VITE_APP_URL
    },
  },
  {
    // VH-3273: the Buyer Value Dashboard. Vector admins only for now, so its figures can be
    // checked against real hospitals before customers see it; it's meant for hospital admins
    // (its endpoint is role:provider_admin, which CASL's `manage User` mirrors). No
    // layoutWrapperClasses: an ordinary scrolling page, like iv-inbox.
    path: '/provider/:provider_uuid/value',
    name: 'provider-value-dashboard',
    component: () => import('@/pages/provider/insurance/ValueDashboardPage.vue'),
    meta: { screen: BehaviorScreen.ProviderValueDashboard, layout: 'provider', title: 'Account Value', resource: 'Provider', action: 'manage', subject: 'User' },
    beforeEnter() { redirectIfNotVectorAdmin() },
  },
  {
    // VH-2357: the durable, patient-centric URL — carries a merged plan's stable plan_uuid
    // (Insurance::syntheticPlanUuid, server-side) rather than a VerificationSession/
    // DiscoverySession uuid. Unlike those, this identity never changes across a re-verify or a
    // fresh discovery pass, so switching between a patient's plans (or refreshing/sharing the
    // link) no longer depends on which underlying session happens to currently back the plan.
    //
    // Still WorkspacePage.vue: VH-2263 briefly renamed this to LookupPage.vue, but after the
    // VH-2357 rebuild the entry form lives in LookupFlow.vue and this file is purely the plan
    // workspace — so the workspace name is the accurate one, and keeping the path unchanged
    // keeps merges from main a plain same-path merge rather than a rename detection.
    path: '/provider/:provider_uuid/insurance/patient/:patient_uuid/plan/:plan_uuid',
    name: 'iv-plan-workspace',
    component: () => import('@/pages/provider/insurance/WorkspacePage.vue'),
    meta: { screen: BehaviorScreen.IvPlanWorkspace, layout: 'provider', title: 'Insurance Verification', resource: 'Provider', action: 'read', subject: 'VerificationSession', layoutWrapperClasses: ['layout-content-height-fixed', 'layout-footer-hidden'] },
    beforeEnter: to => guardInsuranceVerificationEntry(to),
  },
  {
    // Legacy discovery-session links. There is no session-uuid-keyed destination any more
    // (VH-2357: plan_uuid is the identity for every plan, discovery-only ones included), and
    // a discovery uuid can't be resolved to a patient/plan pair without a fetch the router
    // has no business doing — so these land on the lookup entry rather than 404.
    path: '/provider/:provider_uuid/insurance/discovery/:uuid',
    redirect: (to: RouteLocationNormalized) => ({ name: 'iv-lookup', params: { provider_uuid: to.params.provider_uuid } }),
  },
]

export default provider
